import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Modal, Avatar } from "../components/Layout";
import { POD_PURPOSES } from "../lib/bouquet";
import { Plus, Users, ClipboardCopy } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const podInviteText = (p) => `You're invited to join "${p.name}" — my pod on Bouquet, where we keep track of what we're growing and show up for each other's next steps.${p.shared_intention ? `\nOur shared intention: ${p.shared_intention}` : ""}\nJoin here: ${window.location.origin}/join/${p.pod_id}`;

export default function Pods() {
  const [pods, setPods] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", member_emails: "", purposes: [], shared_intention: "" });
  const [createdPod, setCreatedPod] = useState(null);
  const location = useLocation();

  const togglePurpose = (p) => {
    setForm((f) => {
      const has = f.purposes.includes(p.label);
      return {
        ...f,
        purposes: has ? f.purposes.filter((x) => x !== p.label) : [...f.purposes, p.label],
        shared_intention: has || f.shared_intention ? f.shared_intention : p.suggestion,
      };
    });
  };

  useEffect(() => {
    if (location.state?.create) {
      setShowCreate(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const load = () => { api.get("/pods/mine").then((r) => setPods(r.data)).catch(() => setPods([])); };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name your pod");
    try {
      const res = await api.post("/pods", {
        name: form.name, description: form.description,
        member_emails: form.member_emails.split(",").map((e) => e.trim()).filter(Boolean),
        purpose: form.purposes.join(" · "), shared_intention: form.shared_intention,
      });
      setCreatedPod(res.data);
      setShowCreate(false);
      setForm({ name: "", description: "", member_emails: "", purposes: [], shared_intention: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create pod");
    }
  };

  if (!pods) return <div className="animate-pulse text-muted-foreground text-sm">Loading your pods…</div>;

  return (
    <div data-testid="pods-page" className="space-y-8">
      <PageHeader eyebrow="Pods" title="My Pods"
        description="Small trusted groups. Conversation happens in your own group chats — Bouquet keeps the bird's-eye view.">
        <button data-testid="create-pod-button" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
          <Plus className="h-4 w-4" /> Gather a pod
        </button>
      </PageHeader>

      {pods.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3">
          <Users className="h-8 w-8 text-primary mx-auto" />
          <p className="font-display text-xl font-medium">Who are your people?</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">A pod is 3–5 people you trust — a sister circle, family, a mastermind. You form it; nobody assigns you.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pods.map((p) => (
          <Link key={p.pod_id} to={`/pods/${p.pod_id}`} data-testid={`pod-card-${p.pod_id}`}
            className="bg-card border border-border rounded-xl p-6 space-y-3 hover:border-primary transition-colors block">
            <div className="flex items-center justify-between gap-3">
              <p className="font-display text-lg font-medium truncate">{p.name}</p>
              {p.chat_platform && <span className="px-2.5 py-1 rounded-full bg-secondary text-[11px] font-semibold shrink-0">{p.chat_platform}</span>}
            </div>
            {p.description && <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
            <div className="flex items-center justify-between">
              <div className="flex -space-x-2">
                {(p.members || []).map((m) => <Avatar key={m.user_id} user={m} size="h-8 w-8 ring-2 ring-card" />)}
              </div>
              <span className="text-[11px] text-muted-foreground">{p.shared_count || 0} shared {p.shared_count === 1 ? "step" : "steps"}</span>
            </div>
          </Link>
        ))}
      </div>

      {createdPod && (
        <Modal onClose={() => setCreatedPod(null)} testid="pod-created-modal">
          <div className="space-y-4">
            <div>
              <h3 className="font-display text-xl font-medium">"{createdPod.name}" is gathered</h3>
              <p className="text-xs text-muted-foreground mt-1">Invite your people — copy this and paste it wherever your group talks.</p>
            </div>
            <pre className="whitespace-pre-wrap bg-secondary rounded-lg p-4 text-sm font-sans" data-testid="pod-invite-text">{podInviteText(createdPod)}</pre>
            <div className="flex gap-2">
              <button data-testid="pod-created-copy"
                onClick={async () => { await navigator.clipboard.writeText(podInviteText(createdPod)); toast.success("Copied — paste it in your group chat"); }}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5">
                <ClipboardCopy className="h-4 w-4" /> Copy invite
              </button>
              <Link to={`/pods/${createdPod.pod_id}`} data-testid="pod-created-open"
                className="flex-1 border border-border py-3 rounded-full text-sm font-semibold hover:bg-secondary transition-colors text-center">Open pod</Link>
            </div>
          </div>
        </Modal>
      )}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} testid="create-pod-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Gather a pod</h3>
            <input data-testid="pod-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pod name — e.g. Sister Circle" className={inputCls} />
            <textarea data-testid="pod-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} placeholder="What brings us together?" className={inputCls} />
            <div className="space-y-2">
              <p className="text-sm font-semibold">Pod purpose <span className="font-normal text-muted-foreground">(optional — pick any that fit; a starting point, not a commitment)</span></p>
              <div className="flex flex-wrap gap-2">
                {POD_PURPOSES.map((p) => (
                  <button key={p.key} type="button" data-testid={`purpose-${p.key}`} onClick={() => togglePurpose(p)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.purposes.includes(p.label) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">Our shared intention <span className="font-normal text-muted-foreground">(optional)</span></label>
              <textarea data-testid="pod-intention-input" value={form.shared_intention}
                onChange={(e) => setForm({ ...form, shared_intention: e.target.value })}
                rows={2} placeholder="What this pod is for, in your own words…" className={inputCls} />
              {(() => {
                const ideas = [...new Set(POD_PURPOSES.filter((p) => form.purposes.includes(p.label)).flatMap((p) => p.gatherings))];
                return ideas.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1.5" data-testid="gathering-ideas">
                    Gathering ideas if helpful: {ideas.join(" · ")}
                  </p>
                );
              })()}
            </div>
            <input data-testid="pod-emails-input" value={form.member_emails} onChange={(e) => setForm({ ...form, member_emails: e.target.value })}
              placeholder="Member emails, comma-separated (must be Bouquet members)" className={inputCls} />
            <button data-testid="create-pod-submit" onClick={create}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Gather</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
